import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { useSearchParams } from 'react-router-dom'

import { isCallInUse as getIsCallInUse } from 'lib/conferenceCall'
import cookie from 'lib/cookie'
import {
  useGetCallDetailsQuery,
  useGetRTMTokenMutation,
  useStartCallMutation,
} from 'store/api/yay/call'
import { useCurrentUser } from 'store/api/yay/user'
import { selectIsLoggedIn } from 'store/selectors'
import { setRTMToken } from 'store/slices/agora'

export const useUserId = () => {
  const currentUserId = useCurrentUser()?.data?.id
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const data: { userId: string; chatUuid?: string } = useMemo(() => {
    const chatUuid = cookie.chatUuid.get()

    return {
      userId: isLoggedIn ? currentUserId?.toString() : chatUuid,
      chatUuid,
    }
  }, [currentUserId, isLoggedIn])

  return data
}

export const useCallRouteParams = () => {
  const { id: conferenceId } = useParams()
  const [searchParams] = useSearchParams()

  // redirect will be used for navigate user back to previous url when call is close
  // invite will be used for 1-1 call, to send a VoIP push notification to called user
  // target will be used for analytics purposes to track chat_room
  const redirectUrl = searchParams.get('redirect')
    ? window.atob(searchParams.get('redirect'))
    : undefined
  const inviteToChatId = searchParams.get('invite')
  const targetChatRoomId = searchParams.get('target')

  const params = useMemo(
    () => ({
      conferenceId,
      redirectUrl,
      inviteToChatId,
      targetChatRoomId,
    }),
    [conferenceId, searchParams]
  )

  return params
}

export const useConferenceCall = () => {
  const { conferenceId } = useCallRouteParams()

  return useGetCallDetailsQuery({
    conferenceId,
  })?.data
}

export const useCloseCallWindow = () => {
  const { redirectUrl } = useCallRouteParams()
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const closeWindow = useCallback(() => {
    if (redirectUrl || !isLoggedIn) {
      window.location.href = redirectUrl || '/'
    } else {
      window.close()
    }
  }, [redirectUrl, isLoggedIn])

  return closeWindow
}

export const useStartCall = () => {
  const dispatch = useDispatch()
  const { conferenceId } = useCallRouteParams()
  const isCallInUse = useRef(getIsCallInUse())
  const isLoggedIn = useSelector(selectIsLoggedIn)

  const closeWindow = useCloseCallWindow()
  const [startCall, startCallMutation] = useStartCallMutation()
  const [getRTMToken, getRTMTokenMutation] = useGetRTMTokenMutation()

  const [isTokenReady, setIsTokenReady] = useState(false)

  const callQuery = useGetCallDetailsQuery(
    { conferenceId },
    {
      skip:
        !startCallMutation.isSuccess ||
        !getRTMTokenMutation.isSuccess ||
        !isTokenReady,
    }
  )

  const _getRTMToken = async () => {
    const result = await getRTMToken({
      conferenceId,
      isAnonymous: !isLoggedIn,
    })

    if ('data' in result) {
      const token = { callId: conferenceId, ...result.data }
      dispatch(setRTMToken(token))

      return token
    }
  }

  useEffect(() => {
    if (startCallMutation.isError) {
      closeWindow()
    }
  }, [startCallMutation.isError])

  useEffect(() => {
    ;(async () => {
      if (conferenceId && !isCallInUse.current) {
        if (isLoggedIn) {
          const result = await startCall({ conferenceId })
          if ('data' in result) {
            cookie.chatUuid.set(result.data.conference_call_user_uuid ?? '')
            await _getRTMToken()
          }
        } else {
          const token = await _getRTMToken()
          cookie.chatUuid.set(token.agora_uid ?? '')

          await startCall({ conferenceId, chatUuid: token.agora_uid })
        }

        setTimeout(() => {
          setIsTokenReady(true)
        }, 1000)
      }
    })()
  }, [conferenceId, isLoggedIn])

  return (
    startCallMutation.isSuccess &&
    getRTMTokenMutation.isSuccess &&
    callQuery.isSuccess
  )
}
